import { useState } from "react";
import { CopyWithSourcesButton } from "@/components/citations/CopyWithSourcesButton";
import { DocumentsConsulted } from "@/components/citations/DocumentsConsulted";
import { AgentTimeline } from "@/components/thread/AgentTimeline";
import { DeepResearchPanel } from "@/components/thread/DeepResearchPanel";
import { MessageRenderer } from "@/components/thread/MessageRenderer";
import { ErrorCard, TerminatedNotice } from "@/components/thread/MessageStatusCards";
import { hasCopyableSources } from "@/lib/copyWithSources";
import { type UiMessage, useChatStore } from "@/runtime/chatStore";

const ARTIFACT_MIN_CHARS = 800;

const HEADER_ACTION_CLASS =
  "type-caption rounded-[var(--radius-pill)] px-[var(--space-2)] py-[var(--space-1)] text-[var(--text-tertiary)] opacity-0 transition-[opacity,background-color,color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-secondary)] focus-visible:opacity-100 focus-visible:shadow-[var(--elev-focus)] focus-visible:outline-none group-hover:opacity-100 [@media(hover:none)]:opacity-100 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[var(--text-tertiary)]";

function OpenArtifactButton({ messageId }: { messageId: string }) {
  const setActive = useChatStore((s) => s.setActiveArtifactMessageId);
  return (
    <button
      type="button"
      onClick={() => setActive(messageId)}
      aria-label="Open answer in side panel"
      className="type-caption inline-flex items-center gap-[var(--space-1)] rounded-[var(--radius-pill)] px-[var(--space-2)] py-[var(--space-1)] text-[var(--text-tertiary)] opacity-0 transition-[opacity,background-color,color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-secondary)] focus-visible:opacity-100 focus-visible:shadow-[var(--elev-focus)] focus-visible:outline-none group-hover:opacity-100 [@media(hover:none)]:opacity-100"
    >
      Open
      <svg
        width="11"
        height="11"
        viewBox="0 0 14 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M4.5 9.5L9.5 4.5M5 4.5h4.5V9" />
      </svg>
    </button>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function onClick() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard write may fail in insecure contexts; user can copy manually */
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={copied ? "Copied" : "Copy message"}
      className={HEADER_ACTION_CLASS}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function Message({ message, isStreaming = false }: { message: UiMessage; isStreaming?: boolean }) {
  const isUser = message.role === "user";
  const sourcesReady = hasCopyableSources(message.citations);
  return (
    <article
      id={message.id}
      className={`group bk-anim-message ${
        isUser
          ? "self-end max-w-[85%] rounded-[var(--radius-lg)] bg-[var(--surface-raised)] px-[var(--space-4)] py-[var(--space-3)] shadow-[var(--elev-ring)] transition-shadow duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:shadow-[var(--elev-ring-strong),var(--elev-whisper)] sm:max-w-[80%]"
          : "py-[var(--space-1)]"
      }`}
    >
      <header className="mb-[var(--space-2)] flex items-center justify-between">
        {isUser ? <span className="type-overline text-[var(--text-tertiary)]">You</span> : <span aria-hidden="true" />}
        <span className="flex items-center gap-[var(--space-1)]">
          {!isUser && message.content.length >= ARTIFACT_MIN_CHARS && <OpenArtifactButton messageId={message.id} />}
          {message.content && <CopyButton text={message.content} />}
          {!isUser && message.content && (isStreaming || sourcesReady) && (
            <CopyWithSourcesButton
              content={message.content}
              citations={message.citations}
              disabled={isStreaming || !sourcesReady}
              className={HEADER_ACTION_CLASS}
            />
          )}
        </span>
      </header>
      {message.role === "assistant" && (
        <>
          <AgentTimeline message={message} isStreaming={isStreaming} />
          <DeepResearchPanel message={message} isStreaming={isStreaming} />
        </>
      )}
      {isUser ? (
        <div className="type-body whitespace-pre-wrap text-[var(--text-primary)]">{message.content}</div>
      ) : (
        // No Sources card here by design: the inline pill is the single citation
        // surface (ADR-124 §7 measured the card drawing a bbox wider than the page
        // image). Re-adding one is pinned as a failure by Message.citationSurface.test.tsx.
        <div className="text-[var(--text-primary)]">
          <MessageRenderer
            content={message.content}
            citations={message.citations}
            unverifiedClaims={message.unverifiedClaims}
          />
          <DocumentsConsulted documents={message.documentsConsulted} citations={message.citations} />
        </div>
      )}
      {message.error && <ErrorCard error={message.error} />}
      {message.terminated_reason && !message.error && <TerminatedNotice reason={message.terminated_reason} />}
    </article>
  );
}
